import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { MenuIcon, PlusIcon, SearchIcon } from "lucide-react";
import type React from "react";
import type { UserProfile } from "@/features/auth/contexts/auth/context";
import { useActiveTeam } from "@/services/teams/useActiveTeam";
import { isFeatureEnabled } from "@/services/feature-flags";
import { Button } from "@/components/ui/button";
import { LanguageToggle } from "@/components/shared/LanguageToggle";
import { routeTitleKey } from "./nav-model";

/**
 * Topbar — the 60px header over the scrolling content: a breadcrumb
 * (workspace › current page), the ⌘K command trigger, the language toggle, and
 * the primary "New audit" action. On mobile it also carries the hamburger that
 * opens the sidebar drawer. Notifications ("Sygnały") are intentionally omitted
 * until that feature exists.
 */
export function Topbar({
  profile,
  pathname,
  onOpenCommand,
  onOpenMobileNav,
}: {
  profile: UserProfile | null;
  pathname: string;
  onOpenCommand: () => void;
  onOpenMobileNav: () => void;
}): React.ReactElement {
  const { t } = useTranslation("common");
  const navigate = useNavigate();
  const { activeTeam } = useActiveTeam(profile);

  // Teams gated off by default (pirx-qy6v4): never surface a team name in the
  // breadcrumb while teams are hidden — fall back to the brand.
  const workspaceLabel =
    (isFeatureEnabled("TEAMS") ? activeTeam?.name : null) ?? "Vardic";
  const titleKey = routeTitleKey(pathname);

  return (
    <header
      className="flex h-[var(--topbar-h)] shrink-0 items-center gap-3 border-b bg-[var(--surface)] px-4 sm:px-6"
      data-test="app-topbar"
    >
      <Button
        aria-label={t("nav.openMenu")}
        className="md:hidden"
        data-test="open-mobile-nav"
        onClick={onOpenMobileNav}
        size="icon"
        variant="ghost"
      >
        <MenuIcon />
      </Button>

      <div className="flex min-w-0 items-center gap-2 text-sm">
        <span className="truncate font-medium text-[var(--ink-3)] max-sm:hidden">
          {workspaceLabel}
        </span>
        <span className="text-[var(--ink-4)] max-sm:hidden">/</span>
        <span className="truncate font-semibold text-[var(--ink)]">
          {titleKey ? t(titleKey) : ""}
        </span>
      </div>

      <div className="flex-1" />

      <button
        className="relative flex h-[38px] w-[280px] items-center gap-2 rounded-[var(--r-sm)] border bg-[var(--surface-2)] px-3 text-sm text-[var(--ink-3)] transition-colors hover:bg-[var(--surface)] max-md:hidden"
        data-test="command-trigger"
        onClick={onOpenCommand}
        type="button"
      >
        <SearchIcon className="size-4 shrink-0" />
        <span className="flex-1 truncate text-left">{t("shell.search")}</span>
        {/* eslint-disable-next-line i18next/no-literal-string -- decorative keyboard-shortcut glyph */}
        <kbd className="rounded border bg-[var(--surface)] px-1.5 py-0.5 font-[var(--mono)] text-[11px] text-[var(--ink-4)]">
          ⌘K
        </kbd>
      </button>

      <Button
        aria-label={t("shell.search")}
        className="md:hidden"
        data-test="command-trigger-mobile"
        onClick={onOpenCommand}
        size="icon"
        variant="outline"
      >
        <SearchIcon />
      </Button>

      <LanguageToggle />

      <Button
        data-test="new-audit"
        onClick={() => {
          void navigate("/");
        }}
        size="sm"
      >
        <PlusIcon />
        <span className="max-sm:hidden">{t("shell.newAudit")}</span>
      </Button>
    </header>
  );
}
